import { $ } from '../platform/dom';
import { elUp } from '../platform/dom';
import { SFX } from '../platform/audio';
import { STATE } from '../sim/fx';
import { CRAFT_MAX, MAX_LEVEL, availableIds, craftById, levelOf, passives, take, takeSupply, weapons } from '../sim/loadout';
import { PASSIVES, passiveById, passiveMax } from '../data/passives';
import { evolutionById } from '../data/evolutions';
import { unionById } from '../data/unions';
import { weaponById } from '../sim/loadout';
import { banner } from './hud';
import { P } from '../world/player';
import { GRANT } from '../sim/meta-parts';
import { bindCards, card, slotName } from './progress';
import { iconFor } from '../data/icons';
import { MECH_UPGRADES, mechUpById, mechUpMax, mechUpName } from '../data/mech-upgrades';
import { mechUpIds, mechUpLevel } from '../sim/mech-upgrades';
import { improviseById } from '../data/improvise';
import { improviseIds, improviseLevel } from '../sim/improvise';
import { metOffer, metPick } from '../sim/metrics';
import { DRAFT, banishOption, draftHints, rerollDraft, rollDraft } from '../sim/draft';
import { protocolById } from '../data/protocols';
import { protocolIds } from '../sim/protocols';
import { rarityOf } from '../data/rarity';
import { craftLabelFor, drawDraftTools } from './draft-tools';
import { registerScreen } from './screens/router';
/* ============================================================
   升級介面（M4 骨架，M5 接上槽位）
   ============================================================

   `STATE.reward` 是既有的「暫停模擬」旗標（game.ts 的主迴圈會檢查它），
   升級介面沿用同一個旗標而不是新開一個 —— 兩個暫停旗標一定會有某個路徑漏掉其中一個。

   **抽選必須用種子 RNG**：升級選項是對局的一部分，用 Math.random 會讓
   同一顆種子跑出不同的一場，重播測試立刻失效（lint:sim 也會擋）。 */

export var currentChoices: string[] = [];

function defOf(id) {
  return mechUpById(id) || improviseById(id) || protocolById(id) || weaponById(id) || passiveById(id);
}
/** 卡片與橫幅上的名字。機體武裝強化的名字**跟著當前機體走**
    （LANCER-2 要寫「電磁刃」不是「機炮」），所以不是固定字串。 */
function nameOf(def, id) {
  var mu = mechUpById(id);
  return mu ? mechUpName(mu) : (def ? def.n : id);
}

/** 抽一輪。**演算法在 `sim/draft.ts`**（P1 之後）——
    這裡只剩「換算局內分鐘數」這一件屬於呼叫端的事。

    搬過去的三個理由（權重要測得到、埋得到點、被 `lint:sim` 的決定性禁令掃到）
    寫在那個檔的檔頭。在那之前這裡是一段均勻隨機，直接命中毒性殺手
    #6（RNG 無緩解）與 #7（build 定型後選項退化）。

    **進化與融合不在這裡出**（2026-08-04 寶箱改拉霸之後）：它們由
    `sim/chest.ts` 的開箱直接給，照 VS 官方 wiki 的 Evolution 頁。
    所以 P1 的「進化推力 ×4.0」加權的是**前置武裝與鑰匙被動**，不是進化本身。 */
function roll() { return rollDraft(STATE.t / 60); }

export function openLevelUp() {
  if (STATE.reward || (P.pendingLv <= 0 && P.bossUp <= 0)) return;
  /* **Boss 獎勵優先於排隊中的一般升級。** 兩者可能同時成立（Boss 死掉那一瞬間
     掉的經驗球常常剛好讓玩家升級），而先給獎勵才對得上因果 ——
     玩家記得的是「我剛打贏了牠」，中間插一張普通卡會把那個連結沖掉。
     這一次不吃 `pendingLv`：Boss 獎勵是額外的，不是預支下一次升級。 */
  var isBoss = P.bossUp > 0;
  if (isBoss) {
    currentChoices = mechUpIds();
    /* 三條線全滿（一場拿不到那麼多，但不留這條路的話就是靜默的軟鎖）：
       退回一般升級的路徑，不要吃掉玩家的獎勵。 */
    if (!currentChoices.length) { P.bossUp = 0; if (P.pendingLv > 0) openLevelUp(); return; }
    /* Boss 獎勵**不給 Reroll／Banish**：那三張是「每把武器保證出一張」的
       定向發牌（`sim/mech-upgrades.ts`），重抽只會換掉走向、排除更沒有意義。
       能動性工具是用來對付隨機的，這一組本來就不隨機。 */
    openCards(currentChoices, '擊破獎勵', P.bossUp > 1
      ? '機體武裝強化（還有 ' + (P.bossUp - 1) + ' 次）' : '機體武裝強化 · 三選一', false);
    return;
  }
  currentChoices = roll();
  /* 全部滿級又滿槽。**這裡原本是 `P.pendingLv = 0; return;`** ——
     佇列被整批歸零，沒有選單、沒有訊息、沒有錯誤，玩家升了級卻什麼都沒拿到。
     量測顯示這不是理論路徑：一場 31–39 級裡約有 20 級是這樣消失的，
     而且全部消失在第 8 分鐘之後（推導見 `.claude/notes/p2-timeline.md`
     與 `.claude/notes/enjoy-gap-2026-08-05.md`）。

     改成照 `03-in-run-progression.md` §一 給補給。**刻意不開選單**：
     這個狀態下真的沒有選擇可以給，開 20 次只有一張卡的選單只會製造
     01 §五說的選擇疲勞。VS 在同樣的情況把寶箱換成金幣袋，是同一個作法 ——
     寶箱那條路徑（`sim/projectiles.ts` 的「緊急補給」）本來就已經這樣做了，
     這裡只是把升級這條補齊。 */
  /* ---------- 2026-08-06：空池分支改成**應急改裝三選一** ----------

     上面那段註解說的「一場 31–39 級裡約有 20 級是這樣消失的」在十顆種子
     的報表上量到了：等級 31–42、真的開了選單只有 20 次。補給雖然不再是
     靜默歸零，但它**不是決策** —— 而消失的那 20 次全部在第 8 分鐘之後，
     正好是「後期沒東西可選」的那一段。

     所以現在改成從 `data/improvise.ts` 的五項純數值裡發三張。
     它沒有上限，所以這條路徑之後永遠有得選；補給那條保留成
     **最後的保險**（連應急改裝都發不出來時，理論上不會發生）。 */
  if (!currentChoices.length) {
    currentChoices = improviseIds();
    if (currentChoices.length) {
      openCards(currentChoices, 'LV ' + P.lv, P.pendingLv > 1
        ? '應急改裝（還有 ' + (P.pendingLv - 1) + ' 次）' : '應急改裝 · 三選一', true);
      return;
    }
    var gain = takeSupply(P.pendingLv);
    P.pendingLv = 0;
    if (gain > 0) { SFX.level(); banner('緊急補給　結構上限 +' + gain); }
    return;
  }
  openCards(currentChoices, 'LV ' + P.lv, P.pendingLv > 1
    ? '選擇一項強化（還有 ' + (P.pendingLv - 1) + ' 次）'
    : '選擇一項強化（' + currentChoices.map(function (_, i) { return i + 1; }).join(' / ') + '）', true);
}

/** 重抽這一輪。次數在 `sim/draft.ts`，這裡只負責重畫。 */
export function doReroll() {
  if (!STATE.reward || P.chestPicks > 0 || P.bossUp > 0) return;
  var next = rerollDraft(STATE.t / 60);
  if (!next) return;                       // 沒次數了：什麼都不做，不要關掉介面
  currentChoices = next;
  openCards(currentChoices, 'LV ' + P.lv, '重抽後的選項（剩餘 ' + DRAFT.rerolls + ' 次）', true);
}

/** 永久排除第 i 張（本局內）。排除之後**立刻補一張**，
    否則玩家會看到一個空位，而「用掉一次資源換來更少的選項」讀起來像懲罰。 */
export function doBanish(i) {
  if (!STATE.reward || P.chestPicks > 0 || P.bossUp > 0) return;
  var id = currentChoices[i];
  if (!id) return;
  /* 補位走的是同一套權重（被排除的已經進 `DRAFT.banished`，不會再出現），
     而且不消耗 reroll 次數 —— 理由在 `sim/draft.ts` 的 `banishOption`。 */
  var next = banishOption(id, STATE.t / 60);
  if (!next) return;
  currentChoices = next;
  openCards(currentChoices, 'LV ' + P.lv,
    '已排除「' + (defOf(id) ? defOf(id).n : id) + '」（剩餘 ' + DRAFT.banishes + ' 次）', true);
}

/** 把一組項目畫成卡片並打開介面。一般升級與 Boss 獎勵共用這一段 ——
    兩邊各寫一份的話，卡片樣式與綁定遲早會漂移成兩套。 */
/** 標題列右側的狀態欄。**一般升級與寶箱要問的問題不同** ——
    升級問「還要再選幾次」，寶箱問「這箱還能拿幾張」。 */
function metaHtml(isDraft, isChest) {
  var out = '';
  if (isChest) {
    if (P.chestPicks > 0) out += '<div><span>還能拿</span><b class="hot">' + P.chestPicks + '</b></div>';
    out += '<div><span>剩餘選項</span><b>' + P.chestOffer.length + '</b></div>';
    return out;
  }
  if (!isDraft) {
    if (P.bossUp > 1) out += '<div><span>剩餘獎勵</span><b class="hot">' + P.bossUp + '</b></div>';
    return out;
  }
  out += '<div><span>等級</span><b>' + P.lv + '</b></div>';
  if (P.pendingLv > 1) out += '<div><span>待領</span><b class="hot">' + P.pendingLv + '</b></div>';
  return out;
}

var registered = false;

function openCards(ids: string[], lvText, subText, isDraft?: boolean, isChest?: boolean) {
  STATE.reward = true;
  if (!registered) {
    registered = true;
    /* **沒有 onEsc**：升級選卡不能跳過（跳過等於白白丟掉一次成長，
       而玩家一定會在慌亂中按到 Esc）。寶箱同理。 */
    /* `noSpace`：升級與寶箱的選卡**不吃空白鍵**。空白鍵在戰鬥中是衝刺，
       玩家連按著它的那一刻畫面彈出來會被直接選掉一張（使用者 2026-08-06）。
       確認鍵留 Enter 與數字鍵 1–3，兩者都不會在戰鬥中被連按。 */
    registerScreen({ id: 'screenUp', noSpace: true });
  }
  /* **每一個 `$()` 都要防 null。** 瀏覽器端 `$` 是 getElementById，抓不到就是 null，
     而這一段跑在「玩家剛升級」的路徑上 —— 丟例外的話選單整個開不出來，
     模擬卻已經停住（`STATE.reward = true`），畫面直接凍死。
     HTML 與 TS 分兩次存檔的那半秒就會踩到，實際踩過一次。 */
  /* 一般升級要把箱種**屬性整個拿掉**，不能設成空字串 ——
     `[data-tier]` 這個選擇器連空值都會命中，箱色會殘留在下一次升級畫面上。 */
  if (!isChest && (elUp as any).removeAttribute) (elUp as any).removeAttribute('data-tier');
  var eb = $('upEyebrow');
  if (eb) eb.textContent = isChest ? '物資箱 · Cache'
    : (isDraft ? '構裝強化 · Field Refit' : '擊破獎勵 · Boss Bounty');
  /* 張數決定版面（2026-08-06 使用者拍板）：
     **3 張一排、4 張一排、5 張以上每排三張的兩排且排不滿的那排置中**。
     幸運會把寶箱的池子從 5 加到 7（`chest.ts` 的 `LUCK_POOL_MAX`），
     所以「五張以上」是一個範圍不是一個數字 —— CSS 那一側用 `.wrap3`
     的 flex-wrap 處理，張數再變都不用回來改這裡。
     `data-n` 給面板寬度用（四張要比三張寬一點）。 */
  var n = ids.length;
  $('upCards').className = n >= 5 ? 'wrap3' : (n === 4 ? 'four' : '');
  if ((elUp as any).dataset) (elUp as any).dataset.n = String(Math.min(n, 7));
  /* 快選提示要跟著實際張數走。寫死「1–5」而只有三張時，玩家按 4 沒反應
     ——「按了沒反應」永遠會被讀成 bug。 */
  var kk = $('upKeys');
  if (kk) kk.textContent = ids.length > 1 ? ('1–' + ids.length) : '1';
  /* 埋點要記**真正顯示出來的那幾張**，不是 roll() 的候選池 ——
     被選率的分母錯了，07 的兩條紅線（<20% 沒人要、>70% 必選項）就全錯。
     重抽與排除之後補的那一組同樣要記：玩家確實看到了它們。 */
  metOffer(ids);
  var hints = isDraft ? draftHints() : [];
  $('upLv').textContent = lvText;
  $('upSub').textContent = subText;
  var mt = $('upMeta');
  if (mt) mt.innerHTML = metaHtml(isDraft, isChest);
  var html = '';
  ids.forEach(function (id, i) {
    var def: any = defOf(id); if (!def) return;
    var mu = mechUpById(id);
    if (mu) {
      /* 機體武裝強化：**沒有進化、沒有質變、不佔槽**，所以卡面只講兩件事
         ——動的是哪一把、現在幾級。用自己的樣式（`mw`）跟池子的青／綠分開：
         它的來源不同（打贏 Boss 才有），看起來就不該像一張普通升級卡。 */
      var mlv = mechUpLevel(id), mmax = mechUpMax(mu);
      html += card(i, slotName(mu.slot) + ' · Lv' + (mlv + 1), mechUpName(mu), mu.d(mlv + 1),
        mlv === 0 ? '初次強化' : ('提升至 Lv' + (mlv + 1)),
        'mw', { icon: iconFor(mu.ico), axes: mu.axes, pips: { lv: mlv, max: mmax } });
      return;
    }
    /* 應急改裝：跟機體武裝強化同一個形狀（不佔槽、不進池子、沒有前置），
       但**沒有上限**所以不畫等級點陣 —— 畫一條永遠填不滿的進度條
       只會讓玩家以為自己離某個終點還很遠。改成把累計級數寫在註記那一行。 */
    var imp = improviseById(id);
    if (imp) {
      var ilv = improviseLevel(id);
      html += card(i, imp.kind + ' · Lv' + (ilv + 1), imp.n, imp.d(ilv + 1),
        ilv === 0 ? '可選池已見底' : ('已累計 ' + ilv + ' 級'),
        'mod', { icon: iconFor(imp.ico), axes: imp.axes });
      return;
    }
    var lv = levelOf(id), next = lv + 1;
    var ev = evolutionById(id);
    var un = unionById(id);
    var isW = !!weaponById(id);
    var pr = protocolById(id);
    /* 圖示：武裝／進化／融合各有自己的一張；**被動共用「軸」的圖示** ——
       16 個被動各畫一張會讓風格漂移翻三倍，而玩家真正要分辨的
       本來就是「這張卡動的是哪條軸」。 */
    var icon = iconFor(id) || (def.axes && def.axes.length ? iconFor('ax-' + def.axes[0]) : '');
    var opt: any = { icon: icon, axes: def.axes };
    /* 等級點陣要**當前等級與這一項自己的上限**。被動的上限各自不同
       （規則類刻意比較短，見 `passiveMax`），寫死 5 的話那幾張卡會
       永遠顯示「還差兩級」而其實它已經滿了。 */
    if (ev || un) {
      /* 質變也有等級了（`sim/loadout.ts` 的 `CRAFT_MAX`）。點陣要畫出來 ——
         不畫的話「初次進化」與「把進化練深」在卡面上長得一模一樣。 */
      opt.pips = { lv: lv, max: CRAFT_MAX };
    } else if (!pr) {
      opt.pips = { lv: lv, max: isW ? MAX_LEVEL : passiveMax(def) };
    }
    if (isDraft) {
      opt.rar = rarityOf(id);
      /* 這一張拿了就湊齊某個進化／融合 —— 卡面發光並寫出湊齊的是哪一個。
         只寫「快湊齊了」而不寫是什麼的話，玩家還是不知道自己在追什麼。 */
      if (hints.indexOf(id) >= 0) opt.pre = craftLabelFor(id);
      /* ✕ 只在還有次數時畫出來。畫一個按了沒反應的按鈕比不畫更糟。
         **寶箱那邊不給** —— reroll／banish 是升級抽卡的能動性工具，
         寶箱本來就是額外的獎勵，再給一次重抽等於兩套資源互相稀釋。 */
      if (DRAFT.banishes > 0 && !isChest) opt.ban = true;
    }
    if (pr) {
      /* 破格協定：**自己的卡面**（紫色、代價獨立一行、不寫等級）。
         套進「Lv n → Lv n+1」那個模板的話它會讀起來像一個很強的被動，
         而它跟被動的差別正是「這是方向宣告不是加成」。 */
      opt.cost = pr.cost;
      html += card(i, '傳說 · 協定', def.n, def.d(1), '不佔槽 · 一場最多兩個', 'prot', opt);
      return;
    }
    if (ev || un) {
      /* 質變卡有兩種讀法，**卡面必須分得出來**：
         初次（lv 0，來自寶箱）講的是「這一格會變成什麼」，
         練深（lv 1/2，來自升級選單）講的是「哪一條限制接著被拿掉」。
         兩者共用同一張卡面樣式（`evo`）—— 它們是同一件事的兩個階段。 */
      if (def.turn && next === def.turn) opt.turn = def.turnD;
      var kindN = ev ? '進化' : '融合';
      var sub;
      if (lv === 0) {
        if (ev) {
          var from = weaponById(ev.evo.from);
          sub = '取代「' + (from ? from.n : ev.evo.from) + '」';
        } else {
          /* 融合的卡片要講清楚「空出一格」—— 那是它跟進化唯一的機制差別，
             看不出來的話玩家不知道自己為什麼要練滿兩把。 */
          var ua = weaponById(un.union.a), ub = weaponById(un.union.b);
          sub = (ua ? ua.n : un.union.a) + ' + ' + (ub ? ub.n : un.union.b) + ' · 空出一個武器槽';
        }
      } else {
        sub = 'Lv' + lv + ' → Lv' + next;
      }
      html += card(i, lv === 0 ? kindN : (kindN + ' · Lv' + next), def.n, def.d(next), sub, 'evo', opt);
    } else {
      /* 質變提示**只在「再升一級就會發生」時出現**。每一級都掛著的話它會變成
         背景，玩家不會為了它調整選擇 —— 而那正是這個提示存在的唯一理由。 */
      if (def.turn && next === def.turn) opt.turn = def.turnD;
      html += card(i, def.kind + ' · Lv' + next, def.n, def.d(next),
        lv === 0 ? '新獲得' : ('Lv' + lv + ' → Lv' + next), isW ? 'wpn' : 'mod', opt);
    }
  });
  $('upCards').innerHTML = html;
  bindCards($('upCards'), chooseUpgrade, (isDraft && !isChest) ? doBanish : null);
  drawDraftTools(!!isDraft && !isChest, doReroll);
  elUp.classList.add('show');
  /* 前置達成的那一輪換成專屬音效。視覺標記在玩家的眼睛還盯著敵人時會漏掉，
     而這一輪正是他最該停下來看的一輪。 */
  if (hints.length) SFX.preEvo(); else SFX.level();
}

/** 開箱揭示。**東西已經拿到了**（`sim/chest.ts` 的 `openChest` 在撿起來
    這個介面是**真的選擇**（2026-08-04 晚拔掉拉霸之後）：東西還沒套用，
    玩家選了哪張才 `take()` 哪張。金箱是五選三 —— 選完一張，剩下的重畫，
    直到 `P.chestPicks` 用完。

    五選三的價值在**排除**：五張裡挑三張，玩家真正在回答的是
    「我不要剩下那兩張」。三選一連做三次只是重複三次同一個決策。 */
export function openChestReveal() {
  var ids = P.chestOffer;
  if (!ids || !ids.length || P.chestPicks <= 0) return;
  var tierName = P.chestTier === 'gold' ? '金箱' : (P.chestTier === 'bronze' ? '銅箱' : '銀箱');
  /* 走 `openCards` 而不是自己畫一份 —— 卡片樣式與綁定各寫一份的話
     遲早漂移成兩套（這個檔的 `openCards` 檔頭就是為了這件事存在的）。
     `isDraft` 給 true：稀有度、進化前置的發光都要有，
     它們跟一般三選一是同一套語彙。 */
  /* 箱種色掛在畫面上（金／銀／銅）——「這是升級還是開箱」在混戰中要一眼分得開，
     而兩者共用同一套卡片與同一個 `#screenUp`（刻意的，避免樣式漂移）。 */
  /* `dataset` 要防 undefined：headless 的假 DOM（`platform/dom.ts` 的 `stubEl`）
     沒有這個屬性，直接寫會在**測試裡**炸掉（chest.test.ts 會走這條路）。 */
  if ((elUp as any).dataset) (elUp as any).dataset.tier = P.chestTier || 'silver';
  /* 副標描述的是**實際操作**：一張一張選，選完剩下的重畫再問下一張。
     這裡原本寫「挑掉你不要的」—— 那是設計意圖（五選三同時是排除），
     但玩家點下去的是「我要的」，照那句話操作會點錯，所以換掉。 */
  openCards(ids, tierName, ids.length + ' 選 ' + P.chestPicks
    + (P.chestPicks > 1 ? '　·　依序選 ' + P.chestPicks + ' 張' : ''), true, true);
}

/** 選中的卡飛向右下的持有列。**五選三的每一步都要看得出「我剛拿走了什麼」** ——
    原本是整頁重畫，剩下的卡瞬間換位置，玩家分不出自己按到了哪一張。
    純視覺：複製一份卡片飛過去，原始 DOM 不動（它馬上就會被重畫掉）。 */
function flyToSlots(i) {
  if (typeof document === 'undefined') return;
  var host = $('upCards'); if (!host || !host.querySelectorAll) return;
  var card = host.querySelectorAll('.card')[i] as any;
  var dst = $('slots') as any;
  if (!card || !dst || !card.getBoundingClientRect) return;
  var a = card.getBoundingClientRect(), b = dst.getBoundingClientRect();
  var clone = card.cloneNode(true) as any;
  /* `animation:none` 是必要的：`.card` 帶著進場動畫（`cardIn`，起始 opacity 0），
     複製品會**重播一次**那個動畫 —— 於是整段飛行都是透明的，看起來像沒做。
     實看截圖抓到過（卡片區與飛行途中都是空的）。 */
  clone.style.cssText = 'position:fixed;left:' + a.left + 'px;top:' + a.top + 'px;width:' + a.width +
    'px;height:' + a.height + 'px;margin:0;pointer-events:none;z-index:40;animation:none;opacity:1;' +
    'transition:transform .42s cubic-bezier(.4,0,.2,1),opacity .42s ease-out';
  document.body.appendChild(clone);
  /* 目標是持有列的中心。位移用 transform（不動版面），縮到 0.16 —— 
     終點大小要接近持有列那格圖示，不然讀起來是「卡片消失」不是「收進去了」。 */
  var dx = (b.left + b.width / 2) - (a.left + a.width / 2);
  var dy = (b.top + b.height / 2) - (a.top + a.height / 2);
  requestAnimationFrame(function () {
    clone.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(.16)';
    clone.style.opacity = '0.15';
  });
  setTimeout(function () { if (clone.parentNode) clone.parentNode.removeChild(clone); }, 460);
}

/** 從寶箱的選項裡拿走第 i 張。回傳有沒有成功。 */
function takeFromChest(i) {
  var id = P.chestOffer[i];
  if (!id) return false;
  var lv = take(id);
  if (!lv) return false;
  metPick(id);
  banner(nameOf(defOf(id), id) + ' Lv' + lv);
  /* **選過的要從選項裡拿掉**，不然五選三可以把同一張選三次
     （`take` 會回第 2、3 級，看起來完全正常）。 */
  P.chestOffer = P.chestOffer.filter(function (x) { return x !== id; });
  P.chestPicks--;
  return true;
}

export function closeChestReveal() {
  P.chestOffer = []; P.chestPicks = 0; P.chestTier = '';
  elUp.classList.remove('show');
  STATE.reward = false;
  updateSlotsUI();
}

export function chooseUpgrade(i) {
  /* **寶箱的三選一／五選三**走自己的清單（`P.chestOffer`）而不是
     `currentChoices` —— 兩者可能同時存在（開箱的當下剛好也升級了）。

     這一行同時是四支 bot 診斷 harness（level／pressure／flow／metrics）的
     出口：它們全部用 `if (STATE.reward) chooseUpgrade(...)` 這個模式驅動介面。
     少了它，介面會把 `STATE.reward` 永遠留在 true —— 模擬停住、
     迴圈空轉到幀數用盡。拉霸那一版第一次跑就踩到了：報表印出
     「一場 139,399 次升級、存活 1.3 分」。**症狀完全不像死鎖**，所以留這段。 */
  if (P.chestPicks > 0 && P.chestOffer.length) {
    /* **先把飛卡的動畫發出去再改資料**：`takeFromChest` 之後這一輪會整頁重畫，
       原本那張卡的 DOM 就不見了，量不到它的位置。 */
    flyToSlots(i);
    if (!takeFromChest(i)) {
      /* 選不動（理論上不會發生，`offerChest` 已經濾過）：
         **不要留在原地** —— 卡住的介面等於死鎖。 */
      closeChestReveal();
      return;
    }
    if (P.chestPicks > 0 && P.chestOffer.length) {
      updateSlotsUI();
      openChestReveal();          // 還要再選：重畫剩下的
    } else {
      closeChestReveal();
    }
    return;
  }
  if (!STATE.reward || !currentChoices[i]) return;
  /* 一般升級與 Boss 獎勵**也要收卡動畫**（使用者 2026-08-05）。
     跟寶箱同一個理由：選完之後選項瞬間消失，玩家沒有任何「我拿到了它」的畫面 ——
     而它其實有去處（右下角的持有列）。動畫要排在 `take()` 之前發：
     之後這個畫面就關了，量不到那張卡的位置。
     複製品在 `document.body` 上，畫面關掉之後它照樣飛完（那正是想要的：
     卡片從已經消失的選單飛進 HUD）。 */
  flyToSlots(i);
  var id = currentChoices[i];
  var lv = take(id);
  if (!lv) return;                        // 理論上不會發生（roll 已經濾過）
  metPick(id);                            // 成對記錄：分母在 openCards
  var def = defOf(id);
  banner(nameOf(def, id) + ' Lv' + lv);

  elUp.classList.remove('show');
  STATE.reward = false;
  /* 扣的是哪一個佇列，看選到的是什麼 —— Boss 獎勵不吃 `pendingLv`，
     否則玩家等於用一次升級換一次獎勵，那就不是獎勵了。 */
  if (mechUpById(id)) P.bossUp--; else P.pendingLv--;
  currentChoices = [];
  updateSlotsUI();
  /* 還有排隊中的等級就再開一次 —— 交給主迴圈下一幀處理（game.ts 每個 tick 都會檢查
     pendingLv）。這裡不能用 setTimeout：模擬層的決定性禁令。 */
}

/** HUD 右下角的持有列。

    **改成圖示格而不是文字列**（使用者 2026-08-02：「圖示要顯示在右下角，
    讓玩家一看圖示就知道是什麼被動」）。理由是這一列的閱讀時機是**戰鬥中** ——
    玩家沒有時間讀十一行字，他要的是「我拿了什麼」這個問題的一瞥答案。

    兩排：上排武裝、下排被動。等級用右下角的小數字，滿級換色 ——
    滿級是進化的前提，所以「哪一把已經滿了」是玩家真的會找的資訊。 */
function slotCell(def, id, lv, kind) {
  var ico = iconFor(id) || (def && def.axes && def.axes.length ? iconFor('ax-' + def.axes[0]) : '');
  /* 「滿級」要用**這一格自己的上限**：質變是 `CRAFT_MAX`（3）。
     一律比 `MAX_LEVEL` 的話，練滿的質變永遠不會換色 ——
     而它正是玩家在後半場最想確認「這格還能不能投」的那一格。 */
  var full = lv >= (craftById(id) ? CRAFT_MAX : MAX_LEVEL) ? ' full' : '';
  /* **顏色由稀有度決定，不是由類別**（使用者 2026-08-06：「右下角的圖示要跟
     稀有度圖示同色」）。這跟卡片 08-05 的改版是同一條規則（`styles/cards.css`
     檔頭：「稀有度主導顏色，類別退到圖示色相」）—— 兩個地方講同一件事的話，
     玩家在卡片上認到的顏色才連得到右下角那一格。
     類別沒有消失：圖示本身就帶類別色相，而且武裝／被動本來就分成兩排。 */
  return '<div class="sc ' + kind + ' r-' + rarityOf(id) + full + '" title="' + (def ? def.n : id) + ' Lv' + lv + '">' +
    (ico ? '<img src="' + ico + '" alt="">' : '<i>' + (def ? def.n.slice(0, 1) : '?') + '</i>') +
    '<b>' + lv + '</b></div>';
}
export function updateSlotsUI() {
  var w = '', p = '', i;
  /* 機體武裝強化排在最上面一排，而且**只在拿過之後才出現** ——
     一直掛著三個「Lv0」是噪音，而這一列的閱讀時機是戰鬥中。 */
  var m = '';
  for (i = 0; i < MECH_UPGRADES.length; i++) {
    var mu = MECH_UPGRADES[i], mlv = mechUpLevel(mu.id);
    if (!mlv) continue;
    m += '<div class="sc m r-' + rarityOf(mu.id) + (mlv >= mechUpMax(mu) ? ' full' : '') +
      '" title="' + mechUpName(mu) + ' Lv' + mlv + '">' +
      '<img src="' + iconFor(mu.ico) + '" alt=""><b>' + mlv + '</b></div>';
  }
  /* 破格協定跟機體強化同一排（都是「不佔槽的東西」），只是顏色不同。
     **一定要顯示**：它沒有等級、不在武裝也不在被動列，不畫出來的話
     玩家拿到之後在畫面上找不到任何證據，而它是這一場最大的一個決定。 */
  var xs = protocolIds();
  for (i = 0; i < xs.length; i++) {
    var xd = protocolById(xs[i]);
    if (!xd) continue;
    m += '<div class="sc x r-' + rarityOf(xs[i]) + ' full" title="' + xd.n + ' · ' + xd.cost + '">' +
      '<i>' + xd.n.slice(0, 1) + '</i><b>◆</b></div>';
  }
  for (i = 0; i < weapons.length; i++)
    w += slotCell(weaponById(weapons[i].id), weapons[i].id, weapons[i].lv, 'w');
  for (i = 0; i < passives.length; i++)
    p += slotCell(passiveById(passives[i].id), passives[i].id, passives[i].lv, 'p');
  var el = $('slots'); if (!el) return;
  el.innerHTML = (m ? '<div class="srow">' + m + '</div>' : '')
    + (w ? '<div class="srow">' + w + '</div>' : '')
    + (p ? '<div class="srow">' + p + '</div>' : '');
}
